/**
 * Greedy mesher for CHUNK_SIZE x CHUNK_HEIGHT x CHUNK_SIZE uint8 volumes.
 * Emits non-indexed triangle lists with per-vertex color (atlas stub).
 * Mesh content hash is deterministic for golden tests.
 *
 * Phase 2.5: optional Y-band + grass detail LOD for far-chunk cost cuts.
 */

import { BLOCK, BLOCK_COLOR, isOpaque, isSolid } from "./blocks";
import {
  CHUNK_HEIGHT,
  CHUNK_SIZE,
  type Chunk,
  localIndex,
} from "./chunk";

export interface MeshData {
  positions: Float32Array;
  normals: Float32Array;
  colors: Float32Array;
  /** Triangle count (positions.length / 9). */
  triCount: number;
}

/** Grass / detail tier for distance LOD (Phase 2.5). */
export type GrassDetail = "full" | "sparse" | "none";

export interface MeshOptions {
  /**
   * Inclusive Y band to mesh (clamped to [0, HEIGHT]).
   * Default: full column. Callers may pass a tight band around surface.
   */
  yMin?: number;
  yMax?: number;
  /**
   * Decorative tall-grass density.
   * full = every blade (near), sparse = ~1/4 (mid), none = skip (far).
   */
  grassDetail?: GrassDetail;
}

type Axis = 0 | 1 | 2;

const FACE_NORMALS: Record<string, [number, number, number]> = {
  // axis, polarity
  "0+": [1, 0, 0],
  "0-": [-1, 0, 0],
  "1+": [0, 1, 0],
  "1-": [0, -1, 0],
  "2+": [0, 0, 1],
  "2-": [0, 0, -1],
};

function neighborId(
  chunk: Chunk,
  getNeighbor: ((lx: number, ly: number, lz: number) => number) | null,
  lx: number,
  ly: number,
  lz: number,
): number {
  if (
    lx >= 0 &&
    ly >= 0 &&
    lz >= 0 &&
    lx < CHUNK_SIZE &&
    ly < CHUNK_HEIGHT &&
    lz < CHUNK_SIZE
  ) {
    return chunk.blocks[localIndex(lx, ly, lz)]!;
  }
  if (getNeighbor) return getNeighbor(lx, ly, lz);
  return 0; // air outside without neighbor → outer faces visible
}

/**
 * Scan chunk for first/last non-air Y (inclusive). Empty → null.
 * Used for Y-band meshing so sky + deep empty slices are skipped.
 */
export function solidYBounds(chunk: Chunk): { yMin: number; yMax: number } | null {
  const blocks = chunk.blocks;
  const plane = CHUNK_SIZE * CHUNK_SIZE;
  let yMin = CHUNK_HEIGHT;
  let yMax = -1;
  // Layout: localIndex = lx + lz*SIZE + ly*SIZE*SIZE → Y planes are contiguous
  for (let y = 0; y < CHUNK_HEIGHT; y++) {
    const base = y * plane;
    let any = false;
    for (let i = 0; i < plane; i++) {
      if (blocks[base + i]! !== BLOCK.AIR) {
        any = true;
        break;
      }
    }
    if (any) {
      if (y < yMin) yMin = y;
      if (y > yMax) yMax = y;
    }
  }
  if (yMax < 0) return null;
  return { yMin, yMax };
}

/**
 * Greedy mesh one chunk.
 * @param getNeighbor optional world sampler in local-space offsets that may leave the chunk
 * @param opts Phase 2.5 LOD: Y band + grass detail (defaults preserve golden meshes)
 */
export function meshChunk(
  chunk: Chunk,
  getNeighbor: ((lx: number, ly: number, lz: number) => number) | null = null,
  opts: MeshOptions | null = null,
): MeshData {
  const pos: number[] = [];
  const nor: number[] = [];
  const col: number[] = [];

  const grassDetail: GrassDetail = opts?.grassDetail ?? "full";

  // Y band: pad ±1 so top/bottom faces against air still form correctly.
  let bandMin: number;
  let bandMax: number;
  if (opts?.yMin != null || opts?.yMax != null) {
    bandMin = Math.max(0, opts?.yMin ?? 0);
    bandMax = Math.min(CHUNK_HEIGHT - 1, opts?.yMax ?? CHUNK_HEIGHT - 1);
  } else {
    // Auto band from solid content when full height would waste slices.
    // Keep default full-range behavior for goldens when chunk is dense enough;
    // only shrink when large empty sky/void exists (pad 2).
    const b = solidYBounds(chunk);
    if (b) {
      bandMin = Math.max(0, b.yMin - 2);
      bandMax = Math.min(CHUNK_HEIGHT - 1, b.yMax + 2);
    } else {
      return emptyMesh();
    }
  }
  if (bandMax < bandMin) return emptyMesh();

  const dims = [CHUNK_SIZE, CHUNK_HEIGHT, CHUNK_SIZE] as const;

  // For each axis, for each slice, greedy-merge quads
  for (let axis = 0 as Axis; axis < 3; axis++) {
    const u = ((axis + 1) % 3) as Axis;
    const v = ((axis + 2) % 3) as Axis;
    const x = [0, 0, 0];
    const du = [0, 0, 0];
    const dv = [0, 0, 0];

    const sliceSize = dims[u] * dims[v];
    const mask = new Int16Array(sliceSize); // block id or 0
    const maskSign = new Int8Array(sliceSize); // +1 / -1 face dir

    // Slice range along axis: full X/Z; Y limited to band (+ boundary faces)
    let d0 = 0;
    let d1 = dims[axis];
    if (axis === 1) {
      // Y axis: only slices that touch the band
      d0 = bandMin;
      d1 = bandMax + 1; // face between bandMax and bandMax+1
    }

    for (let d = d0; d <= d1; d++) {
      let n = 0;
      for (let j = 0; j < dims[v]; j++) {
        for (let i = 0; i < dims[u]; i++, n++) {
          x[u] = i;
          x[v] = j;
          x[axis] = d - 1;
          // Outside Y band → treat as air for face formation (no deep/sky faces)
          let a: number;
          let b: number;
          if (axis === 1) {
            const ya = x[1];
            const yb = d;
            a =
              ya < bandMin || ya > bandMax
                ? 0
                : neighborId(chunk, getNeighbor, x[0], ya, x[2]);
            b =
              yb < bandMin || yb > bandMax
                ? 0
                : neighborId(chunk, getNeighbor, x[0], yb, x[2]);
          } else {
            // Horizontal axes: still need full neighbor lookups; skip if both
            // Y coords outside band (no visible surface in band)
            const y = x[1];
            if (y < bandMin || y > bandMax) {
              mask[n] = 0;
              maskSign[n] = 0;
              continue;
            }
            a = neighborId(chunk, getNeighbor, x[0], x[1], x[2]);
            x[axis] = d;
            b = neighborId(chunk, getNeighbor, x[0], x[1], x[2]);
            x[axis] = d - 1;
          }

          const aOp = isOpaque(a);
          const bOp = isOpaque(b);
          if (aOp === bOp) {
            mask[n] = 0;
            maskSign[n] = 0;
          } else if (aOp) {
            mask[n] = a;
            maskSign[n] = 1; // face toward +axis (from a to b)
          } else {
            mask[n] = b;
            maskSign[n] = -1;
          }
        }
      }

      // Greedy merge on mask
      n = 0;
      for (let j = 0; j < dims[v]; j++) {
        for (let i = 0; i < dims[u]; ) {
          const id = mask[n]!;
          const sign = maskSign[n]!;
          if (id === 0) {
            i++;
            n++;
            continue;
          }

          // width
          let w = 1;
          while (
            i + w < dims[u] &&
            mask[n + w] === id &&
            maskSign[n + w] === sign
          ) {
            w++;
          }

          // height
          let h = 1;
          outer: while (j + h < dims[v]) {
            for (let k = 0; k < w; k++) {
              const idx = n + k + h * dims[u];
              if (mask[idx] !== id || maskSign[idx] !== sign) break outer;
            }
            h++;
          }

          // Emit quad
          x[u] = i;
          x[v] = j;
          x[axis] = d;

          du[0] = du[1] = du[2] = 0;
          dv[0] = dv[1] = dv[2] = 0;
          du[u] = w;
          dv[v] = h;

          // Four corners — winding depends on sign
          const c0 = [x[0], x[1], x[2]] as [number, number, number];
          const c1: [number, number, number] = [
            x[0] + du[0],
            x[1] + du[1],
            x[2] + du[2],
          ];
          const c2: [number, number, number] = [
            x[0] + du[0] + dv[0],
            x[1] + du[1] + dv[1],
            x[2] + du[2] + dv[2],
          ];
          const c3: [number, number, number] = [
            x[0] + dv[0],
            x[1] + dv[1],
            x[2] + dv[2],
          ];

          const nk = `${axis}${sign > 0 ? "+" : "-"}`;
          const normal = FACE_NORMALS[nk]!;
          const rgb = BLOCK_COLOR[id] ?? [1, 0, 1];

          // Shade slightly by face for read without a full atlas
          const shade =
            axis === 1 ? (sign > 0 ? 1.05 : 0.75) : axis === 0 ? 0.92 : 0.85;
          const cr = Math.min(1, rgb[0] * shade);
          const cg = Math.min(1, rgb[1] * shade);
          const cb = Math.min(1, rgb[2] * shade);

          const pushVert = (p: [number, number, number]) => {
            // World offset applied by caller via mesh position; local chunk space here
            pos.push(p[0], p[1], p[2]);
            nor.push(normal[0], normal[1], normal[2]);
            col.push(cr, cg, cb);
          };

          const pushTri = (
            a: [number, number, number],
            b: [number, number, number],
            c: [number, number, number],
          ) => {
            pushVert(a);
            pushVert(b);
            pushVert(c);
          };

          if (sign > 0) {
            pushTri(c0, c1, c2);
            pushTri(c0, c2, c3);
          } else {
            pushTri(c0, c2, c1);
            pushTri(c0, c3, c2);
          }

          // Zero mask
          for (let jj = 0; jj < h; jj++) {
            for (let ii = 0; ii < w; ii++) {
              const idx = n + ii + jj * dims[u];
              mask[idx] = 0;
              maskSign[idx] = 0;
            }
          }

          i += w;
          n += w;
        }
      }
    }
  }

  // Half-size tall-grass blades (walk-through; not greedy-meshed as full cubes)
  if (grassDetail !== "none") {
    emitHalfSizeGrass(chunk, pos, nor, col, grassDetail, bandMin, bandMax);
  }
  void isSolid;

  const positions = new Float32Array(pos);
  const normals = new Float32Array(nor);
  const colors = new Float32Array(col);
  return {
    positions,
    normals,
    colors,
    triCount: positions.length / 9,
  };
}

/**
 * Decorative tall grass: half-size footprint, full cell height.
 * Phase 2.3: straight + bent blades; width 0.5 or 1.0 (1–2 half-voxel units);
 * direction varies per column via deterministic hash. Non-solid / walk-through.
 * Phase 2.5: sparse = keep ~1/4 columns (hash gate); Y limited to band.
 */
function emitHalfSizeGrass(
  chunk: Chunk,
  pos: number[],
  nor: number[],
  col: number[],
  detail: GrassDetail = "full",
  yMin = 0,
  yMax = CHUNK_HEIGHT - 1,
): void {
  const rgb = BLOCK_COLOR[BLOCK.TALL_GRASS] ?? [0.48, 0.82, 0.38];
  const sparse = detail === "sparse";

  const pushVert = (
    x: number,
    y: number,
    z: number,
    nx: number,
    ny: number,
    nz: number,
    shade: number,
  ) => {
    pos.push(x, y, z);
    nor.push(nx, ny, nz);
    col.push(
      Math.min(1, rgb[0]! * shade),
      Math.min(1, rgb[1]! * shade),
      Math.min(1, rgb[2]! * shade),
    );
  };

  const pushQuad = (
    ax: number,
    ay: number,
    az: number,
    bx: number,
    by: number,
    bz: number,
    cx: number,
    cy: number,
    cz: number,
    dx: number,
    dy: number,
    dz: number,
    nx: number,
    ny: number,
    nz: number,
    shade: number,
  ) => {
    // two tris, both windings so thin blades read from either side
    pushVert(ax, ay, az, nx, ny, nz, shade);
    pushVert(bx, by, bz, nx, ny, nz, shade);
    pushVert(cx, cy, cz, nx, ny, nz, shade);
    pushVert(ax, ay, az, nx, ny, nz, shade);
    pushVert(cx, cy, cz, nx, ny, nz, shade);
    pushVert(dx, dy, dz, nx, ny, nz, shade);
    pushVert(ax, ay, az, -nx, -ny, -nz, shade * 0.92);
    pushVert(cx, cy, cz, -nx, -ny, -nz, shade * 0.92);
    pushVert(bx, by, bz, -nx, -ny, -nz, shade * 0.92);
    pushVert(ax, ay, az, -nx, -ny, -nz, shade * 0.92);
    pushVert(dx, dy, dz, -nx, -ny, -nz, shade * 0.92);
    pushVert(cx, cy, cz, -nx, -ny, -nz, shade * 0.92);
  };

  /** Cheap deterministic hash → [0,1). */
  const h01 = (a: number, b: number, c: number): number => {
    let n = (a * 374761393 + b * 668265263 + c * 1442695041) | 0;
    n = (n ^ (n >>> 13)) * 1274126177;
    n = n ^ (n >>> 16);
    return (n >>> 0) / 4294967296;
  };

  const ly0 = Math.max(0, yMin);
  const ly1 = Math.min(CHUNK_HEIGHT - 1, yMax);

  for (let ly = ly0; ly <= ly1; ly++) {
    for (let lz = 0; lz < CHUNK_SIZE; lz++) {
      for (let lx = 0; lx < CHUNK_SIZE; lx++) {
        if (chunk.blocks[localIndex(lx, ly, lz)] !== BLOCK.TALL_GRASS) continue;

        // Only emit at column base (bottom of contiguous grass stack) so
        // multi-high columns share one blade set (cheaper + cleaner silhouette).
        if (ly > 0 && chunk.blocks[localIndex(lx, ly - 1, lz)] === BLOCK.TALL_GRASS) {
          continue;
        }

        // Sparse LOD: keep ~25% of blade columns (stable hash)
        if (sparse && h01(lx, lz, ly + 3) > 0.25) continue;

        // Measure stack height
        let stackH = 0;
        while (
          ly + stackH < CHUNK_HEIGHT &&
          chunk.blocks[localIndex(lx, ly + stackH, lz)] === BLOCK.TALL_GRASS
        ) {
          stackH++;
        }
        if (stackH <= 0) continue;

        const u0 = h01(lx, lz, ly + 11);
        const u1 = h01(lx + 3, lz - 2, ly + 29);
        const u2 = h01(lx - 5, lz + 7, ly + 47);
        // Width 0.5 (thin) or 1.0 (full half-cell span) — 1–2 voxel-width units
        const bladeW = u0 < 0.55 ? 0.5 : 1.0;
        const halfW = bladeW * 0.5;
        // Yaw of primary blade (radians-ish via unit circle)
        const ang = u1 * Math.PI * 2;
        const cAng = Math.cos(ang);
        const sAng = Math.sin(ang);
        // Bent vs straight (~45% bent for variety)
        const bent = u2 < 0.45;
        // Lean amount for bent blades (toward tip)
        const lean = bent ? 0.18 + u0 * 0.28 : 0;
        const leanX = cAng * lean * stackH;
        const leanZ = sAng * lean * stackH;

        const cx = lx + 0.5;
        const cz = lz + 0.5;
        const y0 = ly;
        const y1 = ly + stackH;

        // Primary blade along yaw; secondary crossed ~90°
        // Bottom: centered; top: offset if bent
        const bx0x = cx - sAng * halfW;
        const bx0z = cz + cAng * halfW;
        const bx1x = cx + sAng * halfW;
        const bx1z = cz - cAng * halfW;
        const tx0x = bx0x + leanX;
        const tx0z = bx0z + leanZ;
        const tx1x = bx1x + leanX;
        const tx1z = bx1z + leanZ;

        // Normals roughly perpendicular to blade plane
        pushQuad(
          bx0x, y0, bx0z,
          bx1x, y0, bx1z,
          tx1x, y1, tx1z,
          tx0x, y1, tx0z,
          cAng, 0, sAng,
          0.95,
        );

        // Cross blade (slightly thinner / shorter lean)
        // Sparse: single blade only (half the quads)
        if (sparse) continue;

        const halfW2 = halfW * 0.85;
        const c2 = -sAng;
        const s2 = cAng;
        const lean2 = lean * 0.65;
        const lxOff = c2 * lean2 * stackH;
        const lzOff = s2 * lean2 * stackH;
        const cx0x = cx - s2 * halfW2;
        const cx0z = cz + c2 * halfW2;
        const cx1x = cx + s2 * halfW2;
        const cx1z = cz - c2 * halfW2;
        pushQuad(
          cx0x, y0, cx0z,
          cx1x, y0, cx1z,
          cx1x + lxOff, y1, cx1z + lzOff,
          cx0x + lxOff, y1, cx0z + lzOff,
          c2, 0, s2,
          0.88,
        );

        // Optional third thin upright for dense short tufts
        if (stackH <= 4 && u1 > 0.6) {
          const tw = halfW * 0.45;
          pushQuad(
            cx - tw, y0, cz,
            cx + tw, y0, cz,
            cx + tw + leanX * 0.3, y1, cz + leanZ * 0.3,
            cx - tw + leanX * 0.3, y1, cz + leanZ * 0.3,
            0, 0, 1,
            0.9,
          );
        }
      }
    }
  }
}

/** Stable FNV-1a over quantized mesh buffers for golden seed tests. */
export function hashMeshData(mesh: MeshData): string {
  let h = 0x811c9dc5;
  const mix = (v: number) => {
    // quantize float to reduce tiny drift
    const q = Math.round(v * 1000);
    h ^= (q >>> 0) & 0xff;
    h = Math.imul(h, 0x01000193);
    h ^= (q >>> 8) & 0xff;
    h = Math.imul(h, 0x01000193);
    h ^= (q >>> 16) & 0xff;
    h = Math.imul(h, 0x01000193);
    h ^= (q >>> 24) & 0xff;
    h = Math.imul(h, 0x01000193);
  };
  for (let i = 0; i < mesh.positions.length; i++) mix(mesh.positions[i]!);
  for (let i = 0; i < mesh.normals.length; i++) mix(mesh.normals[i]!);
  for (let i = 0; i < mesh.colors.length; i++) mix(mesh.colors[i]!);
  return (h >>> 0).toString(16).padStart(8, "0");
}

export function emptyMesh(): MeshData {
  return {
    positions: new Float32Array(0),
    normals: new Float32Array(0),
    colors: new Float32Array(0),
    triCount: 0,
  };
}
